// AdminReceiving — HQ stock table, reorder-suggested rows, PO drafts,
// received orders history + the "Review order" modal used when creating
// or editing a PO. Contains the packs vs units math.

import React, { useMemo, useState } from "react";
import { AlertTriangle, Building2, Check, ChevronRight, Eye, FileText, Hash, Inbox, PackageCheck, Pencil, X } from "lucide-react";
import { AMBER, NAVY, TEAL, useInv } from "./constants";
import { SEED_CATALOGUE, SEED_HQ_STOCK, SEED_RECEIPTS, SEED_SUPPLIERS, catItem, supplierName } from "./seed";
import { SectionHeader } from "./ui";
import { InventoryPdfPreview } from "./PdfPreviewModal";
import TableColumnHeader from "../consult/TableColumnHeader";
import { useTableView, applyTableView, uniqueValues } from "../consult/tableView";

export function AdminReceiving() {
  const {
    catalogue: CATALOGUE = SEED_CATALOGUE,
    hqStock: HQ_STOCK = SEED_HQ_STOCK,
    receipts: RECEIPTS = SEED_RECEIPTS,
    suppliers: SUPPLIERS = SEED_SUPPLIERS,
    pos: POS = [],
    adjustments: ADJUSTMENTS = [],
    approvePO,
    updatePO,
    receivePO,
    deletePO,
    adjustStock,
    hqPurchase,
    deleteItem,
    refresh,
    hasToken,
  } = useInv();
  // Review-order modal state — supports both single-item and multi-item
  // review, plus editing an existing draft PO. `review` shape:
  //   { supplierId, poId?, lines: [{ itemId, qty }] }
  const [review, setReview] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewErr, setReviewErr] = useState("");
  const [receivingId, setReceivingId] = useState(null);
  // Stock-adjustment modal state
  const [adjust, setAdjust] = useState(null); // { itemId, currentQty, newQty, reason }
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [adjustErr, setAdjustErr] = useState("");
  // PDF preview state — `{ kind: "po", payload: { supplier, rows, poNumber } }`
  const [pdfPreview, setPdfPreview] = useState(null);
  // Collapsible history sections — default closed so the actionable
  // areas stay above the fold. Pending delivery defaults OPEN when
  // there are outstanding orders so the admin sees them immediately.
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [receivedOpen, setReceivedOpen] = useState(false);
  const [awaitingOpen, setAwaitingOpen] = useState(true);

  // HQ current stock — sortable / filterable / searchable view, same
  // pattern as the Incentives / Expenses tables (shared consult helpers).
  const [hqView, updateHqView] = useTableView("inventory.hqstock.view.v1");
  const hqRows = useMemo(() => CATALOGUE.map((it) => {
    const q = HQ_STOCK[it.id] ?? 0;
    const pq = Number(it.packQty) || 1;
    const packsOnHand = Math.floor(q / pq);
    const short = packsOnHand < (it.hqMin || 0);
    const abundant = packsOnHand >= (it.hqMax || 0) * 0.8;
    return {
      ...it, q, packsOnHand, short, abundant,
      supplierLabel: supplierName(it.supplier, SUPPLIERS),
      statusLabel: short ? "Low" : abundant ? "Healthy" : "OK",
    };
  }), [CATALOGUE, HQ_STOCK, SUPPLIERS]);
  const hqColumns = useMemo(() => ({
    item:     { get: (r) => r.name },
    supplier: { get: (r) => r.supplierLabel },
    code:     { get: (r) => r.code || "" },
    status:   { get: (r) => r.statusLabel },
    onhand:   { get: (r) => r.packsOnHand, type: "number" },
  }), []);
  const hqDisplay = useMemo(
    () => applyTableView(hqRows, hqView, hqColumns, ["item", "supplier", "code"]),
    [hqRows, hqView, hqColumns]
  );
  const uniqSuppliers = useMemo(() => uniqueValues(hqRows, (r) => r.supplierLabel), [hqRows]);
  const uniqItems     = useMemo(() => uniqueValues(hqRows, (r) => r.name), [hqRows]);
  const uniqCodes     = useMemo(() => uniqueValues(hqRows, (r) => r.code || ""), [hqRows]);
  const uniqStatus    = useMemo(() => uniqueValues(hqRows, (r) => r.statusLabel), [hqRows]);

  // Compute which items need to be re-ordered from their supplier.
  // Semantics: min/max/hqMin/hqMax are stored in PACKS. `q` (HQ on-hand)
  // is in UNITS. Convert to whole packs on hand (floor) so the two sides
  // compare in the same currency.
  const reorderRows = CATALOGUE.map((it) => {
    const q = HQ_STOCK[it.id] ?? 0;
    const pq = Number(it.packQty) || 1;
    const packsOnHand = Math.floor(q / pq);
    const packs = Number(it.orderPacks) > 0
      ? Number(it.orderPacks)
      : Math.max((it.hqMax || 0) - packsOnHand, 1);
    return { ...it, q, packsOnHand, short: packsOnHand < (it.hqMin || 0), orderQty: packs };
  }).filter((r) => r.short);

  // Group reorder rows by supplier so admin can generate one PO each.
  const bySupplier = reorderRows.reduce((acc, r) => {
    (acc[r.supplier] ||= []).push(r);
    return acc;
  }, {});

  // Open the review modal for a whole supplier's short items.
  const openReviewForSupplier = (sid, rows) => {
    setReviewErr("");
    setReview({
      supplierId: sid,
      poId: null,
      lines: rows.map((r) => ({ itemId: r.id, qty: r.orderQty })),
    });
  };
  // Open the review modal for a single item (from the row's Reorder pill).
  const openReviewForItem = (it) => {
    setReviewErr("");
    const q = HQ_STOCK[it.id] ?? 0;
    setReview({
      supplierId: it.supplier,
      poId: null,
      lines: [{ itemId: it.id, qty: Number(it.orderPacks) > 0 ? Number(it.orderPacks) : Math.ceil(Math.max(0, it.hqMax - q) / (it.packQty || 1)) }],
    });
  };
  // Reopen an existing draft or submitted PO for editing. We refetch the
  // latest inventory data so the modal always shows current HQ on-hand,
  // min/max and pack sizes — even if the admin updated items on another
  // screen after this PO was created.
  const openReviewForDraft = async (po) => {
    setReviewErr("");
    setReview({
      supplierId: po.supplier,
      poId: po.id,
      lines: (po.items || []).map((li) => ({ itemId: li.item, qty: li.qty })),
    });
    if (refresh) {
      try { await refresh(); } catch { /* stale-data fallback */ }
    }
  };
  const closeReview = () => { setReview(null); setReviewErr(""); };

  const persistReview = async (status /* 'draft' | 'approved' */) => {
    if (!review) return null;
    const validLines = review.lines.filter((l) => l.qty > 0);
    if (!validLines.length) {
      setReviewErr("Add at least one quantity greater than zero.");
      return null;
    }
    setReviewBusy(true);
    setReviewErr("");
    try {
      const payload = validLines.map((l) => ({ item: l.itemId, qty: Number(l.qty) }));
      let saved;
      if (review.poId) {
        saved = await updatePO(review.poId, { items: payload, status });
      } else {
        saved = await approvePO({ supplier: review.supplierId, items: payload, status });
      }
      return saved;
    } catch (e) {
      setReviewErr(e.message || "Could not save order");
      return null;
    } finally {
      setReviewBusy(false);
    }
  };

  const receiveOrder = async (pid) => {
    if (!receivePO) return;
    if (!window.confirm("Mark this PO as received? Stock will be added to HQ and a receipt will be logged.")) return;
    setReceivingId(pid);
    try { await receivePO(pid); } catch (e) { alert(e.message || "Could not mark as received"); } finally { setReceivingId(null); }
  };

  // Open the manual adjustment modal for an HQ item.
  const openAdjust = (it) => {
    const q = HQ_STOCK[it.id] ?? 0;
    const pq = Number(it.packQty) || 1;
    const packsOnHand = Math.floor(q / pq);
    setAdjustErr("");
    // Modal state is in PACKS (matches the on-hand display).
    setAdjust({ itemId: it.id, packQty: pq, currentPacks: packsOnHand, newPacks: packsOnHand, addPacks: 0, reason: "", createPO: true });
  };
  const closeAdjust = () => { setAdjust(null); setAdjustErr(""); };
  const saveAdjust = async () => {
    if (!adjustStock || !adjust) return;
    if (adjust.newPacks === adjust.currentPacks) {
      setAdjustErr("New quantity is the same as the current quantity.");
      return;
    }
    setAdjustBusy(true);
    setAdjustErr("");
    try {
      // Backend stores HQ stock in units — convert PACKS → UNITS.
      const pq = Number(adjust.packQty) || 1;
      await adjustStock({
        scope: "hq",
        item: adjust.itemId,
        new_qty: adjust.newPacks * pq,
        reason: (adjust.reason || "").trim(),
      });
      closeAdjust();
    } catch (e) {
      setAdjustErr(e.message || "Could not save adjustment");
    } finally {
      setAdjustBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
      <SectionHeader
        eyebrow="Admin · HQ warehouse"
        title="HQ stock &amp; reorder"
      />

      {/* Reorder-needed banner — informational summary; click a supplier
          row to open the Review order modal for that batch. */}
      {reorderRows.length > 0 && (
        <div className="mb-4 rounded-lg border-2 p-3 sm:p-4" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm font-semibold text-red-700">
              {reorderRows.length} item{reorderRows.length > 1 ? "s" : ""} below HQ minimum
            </p>
          </div>
          <div className="space-y-2">
            {Object.entries(bySupplier).map(([sid, rows]) => {
              const supplier = SUPPLIERS.find((s) => s.id === sid) || { id: sid, company: supplierName(sid, SUPPLIERS) };
              return (
              <button
                key={sid}
                type="button"
                onClick={() => hasToken && openReviewForSupplier(sid, rows)}
                disabled={!hasToken}
                title={hasToken ? "Click to review & create draft order" : "Sign in as admin to review"}
                data-testid={`review-order-${sid}`}
                className="w-full text-left rounded-md bg-white border border-red-100 p-2.5 hover:border-red-200 hover:bg-red-50/40 transition disabled:opacity-70 disabled:cursor-default">
                {/* Always stack the item(s) below the supplier for
                    consistent layout regardless of group size. */}
                <div className="flex items-center gap-2 mb-1 min-w-0">
                  <Building2 className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>{supplier.company}</p>
                </div>
                <ul className="text-xs">
                  {rows.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-0.5">
                      <span className="min-w-0 truncate font-medium" style={{ color: NAVY }}>{r.name}</span>
                      <span className="tabular-nums text-slate-500 shrink-0">
                        <span className="text-red-600 font-semibold">{r.packsOnHand}</span> / {r.hqMin} <span className="text-slate-400">packs ({Number(r.packQty) || 1})</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </button>
              );
            })}
          </div>
        </div>
      )}

      {/* HQ stock table with per-item Min/Max */}
      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">HQ current stock</p>
      {/* Search + clear — same toolbar pattern as Incentives / Expenses */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          data-testid="hq-stock-search"
          type="text"
          value={hqView.search || ""}
          onChange={(e) => updateHqView({ search: e.target.value })}
          placeholder="Search item, supplier, code…"
          className="flex-1 min-w-[200px] h-9 px-3 rounded-md border border-slate-300 text-sm focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40"
        />
        {(hqView.sortKey || hqView.filterKey || hqView.search) && (
          <button
            type="button"
            data-testid="hq-stock-view-clear"
            onClick={() => updateHqView({ sortKey: null, sortDir: null, filterKey: null, filterValues: null, search: "" })}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3.5 w-3.5" /> Clear view
          </button>
        )}
        <span data-testid="hq-stock-shown-count" className="text-xs text-slate-500 tabular-nums">
          {hqDisplay.length} of {hqRows.length}
        </span>
      </div>
      <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 mb-6">
        <table className="w-full min-w-[720px] text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <TableColumnHeader columnKey="item" label="Item" values={uniqItems}
                view={hqView} onChange={updateHqView} testId="hq-stock" className="py-2.5 pr-4 font-semibold" />
              <TableColumnHeader columnKey="supplier" label="Supplier" values={uniqSuppliers}
                view={hqView} onChange={updateHqView} testId="hq-stock" className="py-2.5 pr-4 font-semibold" />
              <th className="py-2.5 pr-4 font-semibold text-center">Units</th>
              <TableColumnHeader columnKey="code" label="Ordering code" values={uniqCodes}
                view={hqView} onChange={updateHqView} testId="hq-stock" className="py-2.5 pr-4 font-semibold" />
              <th className="py-2.5 pr-4 font-semibold text-center">HQ Min</th>
              <th className="py-2.5 pr-4 font-semibold text-center">HQ Max</th>
              <TableColumnHeader columnKey="onhand" label="On hand" type="number"
                view={hqView} onChange={updateHqView} testId="hq-stock" align="right" className="py-2.5 pr-4 font-semibold" />
              <TableColumnHeader columnKey="status" label="Status" values={uniqStatus}
                view={hqView} onChange={updateHqView} testId="hq-stock" align="right" className="py-2.5 pr-4 font-semibold" />
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {hqDisplay.length === 0 && (
              <tr data-testid="hq-stock-no-matches">
                <td colSpan={9} className="py-10 text-center text-sm text-slate-500 italic">
                  No items match your search or filter.
                </td>
              </tr>
            )}
            {hqDisplay.map((it) => {
              const { q, packsOnHand, short, abundant } = it;
              return (
                <tr key={it.id} className={`border-b border-slate-100 hover:bg-[#F0F5F4]/60 transition ${short ? "bg-red-50/40" : ""}`}>
                  <td className="py-3 pr-4 font-semibold" style={{ color: NAVY }}>{it.name}</td>
                  <td className="py-3 pr-4 text-slate-600 text-xs">{supplierName(it.supplier, SUPPLIERS)}</td>
                  <td className="py-3 pr-4 text-center">
                    <span className="inline-flex items-center justify-center h-6 px-2 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 tabular-nums">
                      {Number(it.packQty) || 1}
                    </span>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-600">{it.code || "—"}</td>
                  <td className="py-3 pr-4 text-center tabular-nums text-slate-500">{it.hqMin}</td>
                  <td className="py-3 pr-4 text-center tabular-nums text-slate-500">{it.hqMax}</td>
                  <td className="py-3 pr-4 text-right tabular-nums font-semibold">
                    <button
                      onClick={() => openAdjust(it)}
                      disabled={!hasToken}
                      data-testid={`adjust-hq-btn-${it.id}`}
                      title={hasToken ? `Adjust on-hand count (${q} units)` : "Sign in as admin to adjust"}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md hover:bg-slate-100 disabled:opacity-50 tabular-nums font-semibold"
                      style={{ color: short ? "#B91C1C" : NAVY }}>
                      {packsOnHand}
                      <Pencil className="h-3 w-3 text-slate-400" />
                    </button>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    {short ? (
                      <button
                        onClick={() => openReviewForItem(it)}
                        disabled={!hasToken}
                        title={hasToken ? "" : "Sign in as admin"}
                        data-testid={`reorder-btn-${it.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-red-50 text-red-600 border border-red-200 text-xs font-semibold hover:bg-red-100 transition disabled:opacity-50">
                        <AlertTriangle className="h-3 w-3" /> Reorder
                      </button>
                    ) : abundant ? (
                      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-semibold" style={{ background: `${TEAL}14`, color: TEAL }}>
                        <Check className="h-3 w-3" /> Healthy
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-semibold" style={{ background: `${AMBER}1A`, color: "#78350F" }}>
                        OK
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-2 text-right">
                    <button
                      onClick={async () => {
                        if (!deleteItem) return;
                        if (!window.confirm(`Delete item "${it.name}"?\n\nThis removes it from HQ and every team's stock. Cannot be undone.`)) return;
                        try { await deleteItem(it.id); } catch (e) { alert(e.message); }
                      }}
                      disabled={!hasToken}
                      data-testid={`delete-item-btn-${it.id}`}
                      title={hasToken ? "Delete this item" : "Sign in as admin to delete"}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-xs text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Draft orders — collapsible dropdown to match Purchase orders,
          Pending delivery and Orders received. Always visible (shows 0
          when empty) so the section stays consistent. */}
      {(() => {
        const drafts = POS.filter((p) => p.status === "draft");
        return (
          <>
            <button
              onClick={() => setDraftsOpen((v) => !v)}
              disabled={drafts.length === 0}
              data-testid="toggle-draft-orders-btn"
              className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition mt-2 disabled:opacity-70 disabled:cursor-not-allowed">
              <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Draft orders</span>
              <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
                {drafts.length}
                <ChevronRight className={`h-4 w-4 transition-transform ${drafts.length && draftsOpen ? "rotate-90" : ""}`} />
              </span>
            </button>
            {drafts.length > 0 && draftsOpen && (
            <div className="mt-2 space-y-2 mb-4">
              {drafts.map((po) => {
                const supplier = SUPPLIERS.find((s) => s.id === po.supplier) || { id: po.supplier, company: supplierName(po.supplier, SUPPLIERS) };
                const rows = (po.items || []).map((line) => {
                  const it = CATALOGUE.find((c) => c.id === line.item) || { id: line.item, name: line.item, code: "", pack: "Each", packQty: 1, hqMin: 0, hqMax: 0 };
                  return { ...it, q: HQ_STOCK[it.id] ?? 0, orderQty: line.qty };
                });
                return (
                  <div key={po.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2 flex-nowrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>
                        <span className="inline-flex items-center h-5 px-2 rounded-md text-[10px] font-semibold mr-2" style={{ background: "#E2E8F0", color: "#475569" }}>DRAFT</span>
                        {supplier.company}
                      </p>
                      <p className="mt-0.5 font-mono text-[11px] text-slate-500 truncate">{po.po_number || "— no PO # yet —"}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500 truncate">{po.at || "—"} · {rows.length} item{rows.length !== 1 ? "s" : ""}</p>
                    </div>
                    <button
                      onClick={async () => {
                        if (!updatePO) return;
                        const next = window.prompt("Rename PO number", po.po_number || "");
                        if (next == null) return;
                        const trimmed = next.trim();
                        if (!trimmed) { alert("PO number cannot be empty"); return; }
                        try { await updatePO(po.id, { po_number: trimmed }); }
                        catch (e) { alert(e.message || "Could not rename PO"); }
                      }}
                      disabled={!hasToken}
                      aria-label="Rename PO number"
                      data-testid={`rename-draft-${po.id}`}
                      title={hasToken ? "Rename PO number" : "Sign in as admin"}
                      className="shrink-0 inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-md text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      <Hash className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">PO #</span>
                    </button>
                    <button
                      onClick={() => openReviewForDraft(po)}
                      disabled={!hasToken}
                      aria-label="Review draft"
                      data-testid={`edit-draft-${po.id}`}
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 h-8 w-8 sm:w-auto sm:px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: NAVY }}>
                      <Eye className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Review</span>
                    </button>
                  </div>
                );
              })}
            </div>
            )}
          </>
        );
      })()}

      {/* Recent stock adjustments — removed per user request. Buying
          extra HQ stock now goes through the normal PO → Pending
          delivery → Mark received flow, same as approved team requests. */}

      {/* Purchase orders — bucketed by status so the admin sees drafts
          to keep working on, outstanding orders awaiting delivery, and a
          received-orders archive for reconciliation. */}
      {POS.length > 0 && (() => {
        const awaiting = POS.filter((p) => p.status === "approved");
        const received = POS.filter((p) => p.status === "received");
        const enrich = (po) => {
          const supplier = SUPPLIERS.find((s) => s.id === po.supplier) || { id: po.supplier, company: supplierName(po.supplier, SUPPLIERS) };
          const rows = (po.items || []).map((line) => {
            const it = CATALOGUE.find((c) => c.id === line.item) || { id: line.item, name: line.item, code: "", pack: "Each", packQty: 1, hqMin: 0, hqMax: 0 };
            return { ...it, q: HQ_STOCK[it.id] ?? 0, orderQty: line.qty };
          });
          const totalQty = po.totalQty ?? rows.reduce((s, r) => s + (r.orderQty || 0), 0);
          return { po, supplier, rows, totalQty };
        };
        return (
          <>
            {received.length > 0 && (
              <>
                <button
                  onClick={() => setReceivedOpen((v) => !v)}
                  data-testid="toggle-received-archive-btn"
                  className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition mt-6">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Purchase orders</span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
                    {received.length}
                    <ChevronRight className={`h-4 w-4 transition-transform ${receivedOpen ? "rotate-90" : ""}`} />
                  </span>
                </button>
                {receivedOpen && (
                <div className="mt-2 space-y-2">
                  {received.map((po) => {
                    const { supplier, rows } = enrich(po);
                    return (
                      <div key={po.id} className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-nowrap">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-slate-500 truncate">{po.po_number || po.id}</p>
                          <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>{supplier.company}</p>
                          <p className="text-[11px] text-slate-500 truncate">
                            Received {(po.receivedAt || "—").split(" ·")[0]}
                          </p>
                        </div>
                        <span className="shrink-0 inline-flex items-center h-6 px-2 rounded-md text-[10px] font-semibold whitespace-nowrap" style={{ background: `${TEAL}18`, color: TEAL }}>RECEIVED</span>
                        <button
                          onClick={() => setPdfPreview({ kind: "po", payload: { supplier, rows, poNumber: po.po_number } })}
                          aria-label="Open PO PDF"
                          data-testid={`po-pdf-${po.id}`}
                          className="shrink-0 inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-md text-xs font-semibold text-white shadow-sm" style={{ background: TEAL }}>
                          <FileText className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">PO PDF</span>
                        </button>
                        <button
                          onClick={async () => {
                            if (!deletePO) return;
                            if (!window.confirm("Delete this received PO from history? Receipts already logged will remain.")) return;
                            try { await deletePO(po.id); } catch (e) { alert(e.message || "Could not delete"); }
                          }}
                          disabled={!hasToken}
                          aria-label="Delete PO"
                          data-testid={`delete-received-${po.id}`}
                          title={hasToken ? "Delete from history" : "Sign in as admin"}
                          className="shrink-0 inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-md text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50">
                          <X className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                )}
              </>
            )}
            {awaiting.length > 0 && (
              <>
                <button
                  onClick={() => setAwaitingOpen((v) => !v)}
                  data-testid="toggle-awaiting-btn"
                  className="w-full flex items-center justify-between gap-2 h-10 px-3 rounded-md border-2 mt-6 hover:brightness-105 transition"
                  style={{ borderColor: `${AMBER}55`, background: `${AMBER}0F` }}>
                  <span className="text-xs uppercase tracking-[0.12em] font-semibold" style={{ color: "#78350F" }}>
                    <Inbox className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                    Pending delivery
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#78350F" }}>
                    {awaiting.length}
                    <ChevronRight className={`h-4 w-4 transition-transform ${awaitingOpen ? "rotate-90" : ""}`} />
                  </span>
                </button>
                {awaitingOpen && (
                <div className="mt-2 space-y-2">
                  {awaiting.map((po) => {
                    const { supplier, rows, totalQty } = enrich(po);
                    // Format the approval date as "Aug 4, 2026" (no time, no
                    // items/units — the user wants a clean 3-line stack).
                    const iso = po.submittedAt_iso || po.at_iso || po.approvedAt_iso;
                    const dateLabel = iso
                      ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : (po.submittedAt || po.at || "—");
                    return (
                      <div key={po.id} className="rounded-md border-2 p-3 flex items-center gap-2 flex-nowrap" style={{ borderColor: `${AMBER}55`, background: `${AMBER}0A` }}>
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs font-semibold truncate" style={{ color: "#78350F" }}>
                            {po.po_number || po.id}
                          </p>
                          <p className="text-sm font-semibold mt-0.5 truncate" style={{ color: NAVY }}>
                            {supplier.company}
                          </p>
                          <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums truncate">
                            {dateLabel}
                          </p>
                          <p className="sr-only">
                            {rows.length} item{rows.length !== 1 ? "s" : ""} · {totalQty} pack{totalQty === 1 ? "" : "s"}
                          </p>
                        </div>
                        <button
                          onClick={() => setPdfPreview({ kind: "po", payload: { supplier, rows, poNumber: po.po_number } })}
                          aria-label="Open PO PDF"
                          data-testid={`po-pdf-${po.id}`}
                          className="shrink-0 inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-md text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                          <FileText className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">PO PDF</span>
                        </button>
                        <button
                          onClick={() => openReviewForDraft(po)}
                          disabled={!hasToken}
                          aria-label="Edit submitted PO"
                          data-testid={`edit-awaiting-${po.id}`}
                          title={hasToken ? "Edit items or quantities" : "Sign in as admin"}
                          className="shrink-0 inline-flex items-center justify-center gap-1.5 h-8 w-8 sm:w-auto sm:px-3 rounded-md text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => receiveOrder(po.id)}
                          disabled={!hasToken || receivingId === po.id}
                          aria-label={receivingId === po.id ? "Receiving…" : "Mark received"}
                          data-testid={`receive-po-${po.id}`}
                          title={hasToken ? "" : "Sign in as admin"}
                          className="shrink-0 inline-flex items-center justify-center gap-1.5 h-8 w-8 sm:w-auto sm:px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                          <PackageCheck className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{receivingId === po.id ? "Receiving…" : "Mark received"}</span>
                        </button>
                        <button
                          onClick={async () => {
                            if (!deletePO) return;
                            if (!window.confirm(`Delete submitted PO ${po.po_number || po.id}? This cannot be undone. HQ stock is not affected because the order has not been received yet.`)) return;
                            try { await deletePO(po.id); } catch (e) { alert(e.message || "Could not delete"); }
                          }}
                          disabled={!hasToken}
                          aria-label="Delete PO"
                          data-testid={`delete-awaiting-${po.id}`}
                          title={hasToken ? "Delete submitted PO" : "Sign in as admin"}
                          className="shrink-0 inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-md text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50">
                          <X className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                )}
              </>
            )}
          </>
        );
      })()}

      {/* Receipts log — sits AFTER Purchase orders + Pending delivery so
          the eye moves top-down through "history of POs → what's in
          transit → line-level receipts logged so far". */}
      <button
        onClick={() => setReceiptsOpen((v) => !v)}
        data-testid="toggle-recent-receipts-btn"
        className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition mt-6">
        <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Orders received</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
          {RECEIPTS.length}
          <ChevronRight className={`h-4 w-4 transition-transform ${receiptsOpen ? "rotate-90" : ""}`} />
        </span>
      </button>
      {receiptsOpen && (
      <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 mt-2">
        <table className="w-full min-w-[720px] text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2.5 pr-4 font-semibold">Date</th>
              <th className="py-2.5 pr-4 font-semibold">Supplier</th>
              <th className="py-2.5 pr-4 font-semibold">Item</th>
              <th className="py-2.5 pr-4 font-semibold text-right">Qty</th>
              <th className="py-2.5 pr-4 font-semibold">PO #</th>
              <th className="py-2.5 pr-4 font-semibold">Received by</th>
            </tr>
          </thead>
          <tbody>
            {RECEIPTS.map((r) => {
              const it = catItem(r.item, CATALOGUE) || { name: r.item };
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-[#F0F5F4]/60 transition">
                  <td className="py-3 pr-4 text-slate-700 tabular-nums">{r.date}</td>
                  <td className="py-3 pr-4 text-slate-700">{supplierName(r.supplier, SUPPLIERS)}</td>
                  <td className="py-3 pr-4 font-semibold" style={{ color: NAVY }}>{it.name}</td>
                  <td className="py-3 pr-4 text-right tabular-nums font-semibold" style={{ color: TEAL }}>+ {r.qty}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-500">{r.po || "N/A"}</td>
                  <td className="py-3 pr-4 text-slate-600">HQ</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Review Order modal — supports single-item, multi-item and
          reopening an existing draft. Save draft persists without a PO
          number; Submit order finalises + downloads the PDF. */}
      {review && (() => {
        const supplier = SUPPLIERS.find((s) => s.id === review.supplierId) || { id: review.supplierId, company: supplierName(review.supplierId, SUPPLIERS) };
        // Identify whether this modal is editing an existing draft or an
        // already-submitted (approved, awaiting delivery) PO. Received POs
        // are never editable — the modal cannot be opened for those.
        const reviewPO = review.poId ? POS.find((p) => p.id === review.poId) : null;
        const isEditingApproved = reviewPO?.status === "approved";
        const lineRows = review.lines.map((l) => {
          const it = CATALOGUE.find((c) => c.id === l.itemId) || { id: l.itemId, name: l.itemId, code: "", pack: "Each", packQty: 1, hqMin: 0, hqMax: 0 };
          const q = HQ_STOCK[it.id] ?? 0;
          return { ...it, q, qty: l.qty };
        });
        const updateLineQty = (idx, val) => {
          setReview((r) => ({
            ...r,
            lines: r.lines.map((l, i) => (i === idx ? { ...l, qty: val === "" ? 0 : Number(val) } : l)),
          }));
        };
        const topUpAll = () => {
          setReview((r) => ({
            ...r,
            lines: r.lines.map((l) => {
              const it = CATALOGUE.find((c) => c.id === l.itemId);
              const q = HQ_STOCK[l.itemId] ?? 0;
              const pq = it ? (Number(it.packQty) || 1) : 1;
              // hqMax is now stored in PACKS. Order = hqMax - packs-on-hand.
              const packs = it ? Math.max((it.hqMax || 0) - Math.floor(q / pq), 0) : l.qty;
              return { ...l, qty: packs };
            }),
          }));
        };
        const totalPacks = lineRows.reduce((s, r) => s + (r.qty || 0), 0);
        const totalUnits = lineRows.reduce((s, r) => s + ((r.qty || 0) * (r.packQty || 1)), 0);
        return (
          <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-slate-900/60 p-3 sm:p-6" onClick={closeReview}>
            <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col" data-testid="review-order-modal">
              <div className="p-5 border-b border-slate-100">
                <p className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: TEAL }}>
                  {isEditingApproved ? "Edit submitted order" : (review.poId ? "Edit draft order" : "Review order")}
                </p>
                <h3 className="text-lg font-semibold mt-1" style={{ color: NAVY }}>{supplier.company}</h3>
                {reviewPO?.po_number && (
                  <p className="mt-0.5 font-mono text-[11px] text-slate-500">{reviewPO.po_number}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">{lineRows.length} item{lineRows.length !== 1 ? "s" : ""} · {totalPacks} pack{totalPacks === 1 ? "" : "s"} · {totalUnits} unit{totalUnits === 1 ? "" : "s"}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {lineRows.map((row, idx) => (
                  <div key={row.id} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate" style={{ color: NAVY }}>{row.name}</p>
                        <p className="text-[11px] text-slate-500 tabular-nums">
                          On hand <span className="text-red-600 font-semibold">{Math.floor(row.q / (row.packQty || 1))}</span> packs ({row.q} units) · Min {row.hqMin} · Max {row.hqMax} · {row.pack} of {row.packQty}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">Packs</label>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        value={row.qty === 0 ? "" : row.qty}
                        placeholder="0"
                        onChange={(e) => updateLineQty(idx, e.target.value)}
                        data-testid={`review-line-qty-${row.id}`}
                        className="h-10 flex-1 min-w-0 w-0 text-right rounded-md border border-slate-300 focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40 tabular-nums font-semibold px-3"
                        style={{ color: NAVY, fontSize: 16 }}
                      />
                      <button
                        onClick={() => updateLineQty(idx, Math.max((row.hqMax || 0) - Math.floor(row.q / (row.packQty || 1)), 0))}
                        className="h-10 shrink-0 px-2.5 rounded-md text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 whitespace-nowrap">
                        To max
                      </button>
                    </div>
                    <p className="mt-1 text-[10px] text-slate-500">
                      {row.qty || 0} × {row.packQty} = <b className="text-slate-700 tabular-nums">{(row.qty || 0) * (row.packQty || 1)}</b> units · brings HQ to {row.q + ((row.qty || 0) * (row.packQty || 1))}
                    </p>
                  </div>
                ))}
                {lineRows.length > 1 && (
                  <button onClick={topUpAll} className="w-full h-9 rounded-md text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">
                    Top up every line to max
                  </button>
                )}
                {reviewErr && (
                  <p className="text-xs text-red-600" data-testid="review-order-err">{reviewErr}</p>
                )}
              </div>
              <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center gap-1.5 flex-nowrap">
                {review.poId && (
                  <button
                    onClick={async () => {
                      if (!deletePO) return;
                      const label = isEditingApproved ? "Delete this submitted PO? HQ stock is not affected because the order has not been received yet." : "Delete this draft order? This cannot be undone.";
                      if (!window.confirm(label)) return;
                      setReviewBusy(true);
                      try {
                        await deletePO(review.poId);
                        closeReview();
                      } catch (e) {
                        setReviewErr(e.message || "Could not delete order");
                      } finally {
                        setReviewBusy(false);
                      }
                    }}
                    disabled={reviewBusy || !hasToken}
                    data-testid="review-delete-draft-btn"
                    title={isEditingApproved ? "Delete submitted PO" : "Delete draft"}
                    className="inline-flex items-center justify-center gap-1 h-9 flex-1 min-w-0 px-2 rounded-md text-[11px] sm:text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50">
                    <X className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Delete</span>
                  </button>
                )}
                <button
                  onClick={closeReview}
                  className="inline-flex items-center justify-center h-9 flex-1 min-w-0 px-2 rounded-md text-[11px] sm:text-xs font-semibold text-slate-600 border border-slate-300 bg-white hover:bg-slate-50">
                  Cancel
                </button>
                {!isEditingApproved && (
                  <button
                    onClick={async () => {
                      const saved = await persistReview("draft");
                      if (saved) closeReview();
                    }}
                    disabled={reviewBusy || !hasToken}
                    data-testid="review-save-draft-btn"
                    title={hasToken ? "" : "Sign in as admin"}
                    className="inline-flex items-center justify-center h-9 flex-1 min-w-0 px-2 rounded-md text-[11px] sm:text-xs font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50">
                    <span className="truncate">{reviewBusy ? "Saving…" : "Save draft"}</span>
                  </button>
                )}
                <button
                  onClick={async () => {
                    const saved = await persistReview("approved");
                    if (saved) closeReview();
                  }}
                  disabled={reviewBusy || !hasToken}
                  data-testid="review-submit-btn"
                  title={hasToken ? "" : "Sign in as admin"}
                  className="inline-flex items-center justify-center gap-1 h-9 flex-1 min-w-0 px-2 rounded-md text-[11px] sm:text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                  <Check className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{reviewBusy ? (isEditingApproved ? "Saving…" : "Submitting…") : (isEditingApproved ? "Save changes" : "Submit")}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Buy more HQ stock — creates a pending PO (approved status) with
          the same paper-trail as approving a team request. Admin then
          marks it received in Pending delivery when the goods arrive. */}
      {adjust && (() => {
        const it = CATALOGUE.find((c) => c.id === adjust.itemId);
        if (!it) return null;
        const addPacks = Number(adjust.addPacks) || 0;
        return (
          <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-slate-900/60 p-3 sm:p-6" onClick={closeAdjust}>
            <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-xl shadow-xl overflow-hidden" data-testid="adjust-stock-modal">
              <div className="p-5 border-b border-slate-100">
                <p className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: TEAL }}>Buy more HQ stock</p>
                <h3 className="text-lg font-semibold mt-1" style={{ color: NAVY }}>{it.name}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {supplierName(it.supplier, SUPPLIERS)} · packs of {adjust.packQty} · currently {adjust.currentPacks} pack{adjust.currentPacks === 1 ? "" : "s"} on hand
                </p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">How many packs?</label>
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={adjust.addPacks || ""}
                    placeholder="0"
                    onChange={(e) => setAdjust((a) => ({ ...a, addPacks: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) }))}
                    data-testid="adjust-add-input"
                    className="mt-1 h-11 w-full text-center rounded-md border border-slate-300 focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40 tabular-nums font-semibold"
                    style={{ color: NAVY, fontSize: 16 }}
                  />
                  <p className="mt-1 text-[11px] text-slate-500 tabular-nums text-center">
                    = {addPacks * (adjust.packQty || 1)} units total when received
                  </p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Note <span className="text-slate-400 normal-case">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={adjust.reason}
                    onChange={(e) => setAdjust((a) => ({ ...a, reason: e.target.value }))}
                    placeholder="e.g. Bulk purchase for Q3, restocking after promo"
                    data-testid="adjust-reason-input"
                    className="mt-1 w-full rounded-md border border-slate-300 focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40 px-3 py-2 text-sm"
                    style={{ color: NAVY, fontSize: 16 }}
                  />
                </div>

                {/* Create PO? — Yes routes through Pending delivery (default);
                    No adds stock immediately (for seeding initial
                    inventory that was already purchased). */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Create purchase order?</p>
                  <div className="grid grid-cols-2 gap-2" data-testid="createpo-toggle">
                    <button
                      type="button"
                      onClick={() => setAdjust((a) => ({ ...a, createPO: true }))}
                      data-testid="createpo-yes"
                      className="h-10 rounded-md text-xs font-semibold border-2 transition"
                      style={{
                        background: adjust.createPO ? TEAL : "#FFFFFF",
                        color: adjust.createPO ? "#FFFFFF" : NAVY,
                        borderColor: adjust.createPO ? TEAL : "#CBD5E1",
                      }}>
                      Yes — create PO
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdjust((a) => ({ ...a, createPO: false }))}
                      data-testid="createpo-no"
                      className="h-10 rounded-md text-xs font-semibold border-2 transition"
                      style={{
                        background: !adjust.createPO ? NAVY : "#FFFFFF",
                        color: !adjust.createPO ? "#FFFFFF" : NAVY,
                        borderColor: !adjust.createPO ? NAVY : "#CBD5E1",
                      }}>
                      No — add directly
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500 leading-snug">
                    {adjust.createPO
                      ? `A pending PO will be created (or merged into today's PO for ${supplierName(it.supplier, SUPPLIERS)}). Stock is added when you tap Mark received.`
                      : "Stock is added to HQ immediately with a receipt entry — no PO. Use this for seeding initial inventory you already have."}
                  </p>
                </div>
                {adjustErr && (
                  <p className="text-xs text-red-600" data-testid="adjust-err">{adjustErr}</p>
                )}
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center gap-2 justify-end">
                <button onClick={closeAdjust} className="h-9 px-3 rounded-md text-xs font-semibold text-slate-600 border border-slate-300">Cancel</button>
                <button
                  onClick={async () => {
                    if (!hqPurchase || addPacks <= 0) {
                      setAdjustErr("Enter a number of packs greater than 0.");
                      return;
                    }
                    setAdjustBusy(true);
                    setAdjustErr("");
                    try {
                      await hqPurchase({
                        item: adjust.itemId,
                        packs: addPacks,
                        note: adjust.reason,
                        create_po: !!adjust.createPO,
                      });
                      closeAdjust();
                    } catch (e) {
                      setAdjustErr(e.message || "Could not save");
                    } finally {
                      setAdjustBusy(false);
                    }
                  }}
                  disabled={adjustBusy || !hasToken || addPacks <= 0}
                  data-testid="adjust-save-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: adjust.createPO ? TEAL : NAVY }}>
                  <Check className="h-3.5 w-3.5" /> {adjustBusy ? "Saving…" : (adjust.createPO ? "Create pending PO" : "Add to HQ stock")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <InventoryPdfPreview
        open={!!pdfPreview}
        kind={pdfPreview?.kind}
        payload={pdfPreview?.payload}
        onClose={() => setPdfPreview(null)}
      />
    </section>
  );
}
